import { format, isValid, parse } from "date-fns";

function areAddressesEqual(firstAddress, secondAddress) {
  return JSON.stringify(firstAddress ?? null) === JSON.stringify(secondAddress ?? null);
}

export const PAYMENT_METHOD_QUERY_PARAMS = {
  content_type: "payment",
  field_name: "payment_method",
  ordering: "order",
};

export const REVIEW_CONTRACT_OPTIONS = [
  { id: true, label: "Yes" },
  { id: false, label: "No" },
];

export function parseDateValue(value) {
  if (!value) {
    return null;
  }

  const parsed = parse(value, "yyyy-MM-dd", new Date());
  return isValid(parsed) ? parsed : null;
}

export function formatDateValue(value) {
  if (!value || !isValid(value)) {
    return null;
  }

  return format(value, "yyyy-MM-dd");
}

const toInputValue = (value) => (value === null || value === undefined ? "" : String(value));

function toPercentInputValue(value) {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  const numericValue = Number(value);

  if (Number.isNaN(numericValue)) {
    return "";
  }

  return String(numericValue * 100);
}

function toBackendSplitValue(value) {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  const numericValue = Number(value);

  if (Number.isNaN(numericValue)) {
    return "";
  }

  const backendValue = numericValue > 1 ? numericValue / 100 : numericValue;

  return String(Number(backendValue.toFixed(2)));
}

export function isValidSplitInput(value, maxDigits = 3) {
  if (!value) {
    return true;
  }

  return value.replace(/\D/g, "").length <= maxDigits;
}

export function formatSplitForDisplay(value) {
  if (value === null || value === undefined || value === "") {
    return "N/A";
  }

  const numericValue = Number(value);

  if (Number.isNaN(numericValue)) {
    return "N/A";
  }

  return `${numericValue * 100}%`;
}

export function mapPaymentToFormState(paymentData) {
  const safePayment = paymentData ?? {};

  return {
    paymentMethod: safePayment.paymentMethod ?? "",
    bankName: safePayment.bankName ?? "",
    routingNumber: toInputValue(safePayment.routingNumber),
    accountNumber: toInputValue(safePayment.accountNumber),
    validFrom: parseDateValue(safePayment.validFrom),
    validTo: parseDateValue(safePayment.validTo),
    reviewContract:
      typeof safePayment.reviewContract === "boolean" ? safePayment.reviewContract : null,
    clientManagementSplit: toPercentInputValue(safePayment.clientManagementSplit),
    providerSplit: toPercentInputValue(safePayment.providerSplit),
    marketingSplit: toPercentInputValue(safePayment.marketingSplit),
    billingAddress: safePayment.billingAddress ?? null,
    mailingAddress: safePayment.mailingAddress ?? null,
    sameAsBillingAddress: areAddressesEqual(
      safePayment.billingAddress ?? null,
      safePayment.mailingAddress ?? null
    ),
  };
}

export function buildPatchPayload(formValues) {
  return {
    paymentMethod: formValues.paymentMethod || "",
    bankName: formValues.bankName?.trim() ?? "",
    routingNumber: formValues.routingNumber?.trim() ?? "",
    accountNumber: formValues.accountNumber?.trim() ?? "",
    validFrom: formatDateValue(formValues.validFrom),
    validTo: formatDateValue(formValues.validTo),
    reviewContract: formValues.reviewContract,
    clientManagementSplit: toBackendSplitValue(formValues.clientManagementSplit?.trim()),
    providerSplit: toBackendSplitValue(formValues.providerSplit?.trim()),
    marketingSplit: toBackendSplitValue(formValues.marketingSplit?.trim()),
    billingAddress: formValues.billingAddress,
    mailingAddress: formValues.mailingAddress,
  };
}

export function buildDirtyPatchPayload(initialPayload, currentPayload) {
  return Object.keys(currentPayload).reduce((dirtyPayload, key) => {
    if (JSON.stringify(initialPayload[key]) !== JSON.stringify(currentPayload[key])) {
      dirtyPayload[key] = currentPayload[key];
    }

    return dirtyPayload;
  }, {});
}

export function formatPaymentMethodOptions(data = []) {
  return data.map((item) => ({
    id: item.key ?? item.id ?? item.value,
    label: item.label ?? item.name ?? item.value ?? "",
  }));
}
